import { type ParsedVttCue } from './parseWebVtt';

const AIR_RELOADED_VTT_CUE_ID_PREFIX = 'air-reloaded-vtt-cue:';

/** Subtitle/caption tracks on the element (e.g. those attached by hls.js). */
export const getSubtitleTextTracks = (mediaElement: Pick<HTMLMediaElement, 'textTracks'>): TextTrack[] =>
  Array.from(mediaElement.textTracks).filter((track) => track.kind === 'subtitles' || track.kind === 'captions');

const getCueTimingKey = (cue: TextTrackCue): string => `${cue.startTime}-${cue.endTime}`;

const removeCueIfPresent = (textTrack: TextTrack, cue: TextTrackCue): void => {
  try {
    textTrack.removeCue(cue);
  } catch (_error) {
    // Cues can be detached by the browser/hls.js while we are replacing them.
  }
};

const removeDuplicateNonReloadedCues = (textTrack: TextTrack): void => {
  const existingCues = textTrack.cues;

  if (!existingCues) {
    return;
  }

  const reloadedCueTimingKeys = new Set<string>();

  for (let index = 0; index < existingCues.length; index += 1) {
    const cue = existingCues[index];

    if (cue?.id.startsWith(AIR_RELOADED_VTT_CUE_ID_PREFIX)) {
      reloadedCueTimingKeys.add(getCueTimingKey(cue));
    }
  }

  for (let index = existingCues.length - 1; index >= 0; index -= 1) {
    const cue = existingCues[index];

    if (cue && !cue.id.startsWith(AIR_RELOADED_VTT_CUE_ID_PREFIX) && reloadedCueTimingKeys.has(getCueTimingKey(cue))) {
      removeCueIfPresent(textTrack, cue);
    }
  }
};

/**
 * Replaces every cue on an existing `TextTrack` with parsed VTT content.
 *
 * Used when `clip.assets.transcriptVtt` is updated in place (same URL, new file) so we can
 * refresh on-screen captions without reloading the HLS stream. The track list comes from
 * hls.js; we only swap the cue payloads the browser renders.
 *
 * Tracks with `mode: 'disabled'` cannot be edited, and replacing cues while the
 * track is `showing` can briefly render old and new captions together. Hide the
 * track while swapping cues, then restore the user's previous mode.
 */
export const replaceTextTrackCues = (textTrack: TextTrack, cues: ParsedVttCue[]): void => {
  const previousMode = textTrack.mode;

  if (previousMode !== 'hidden') {
    textTrack.mode = 'hidden';
  }

  const existingCues = textTrack.cues;

  if (existingCues) {
    for (let index = existingCues.length - 1; index >= 0; index -= 1) {
      const cue = existingCues[index];

      if (cue) {
        textTrack.removeCue(cue);
      }
    }
  }

  cues.forEach((cue, index) => {
    const vttCue = new VTTCue(cue.startTimeSeconds, cue.endTimeSeconds, cue.text);
    vttCue.id = `${AIR_RELOADED_VTT_CUE_ID_PREFIX}${index}`;
    textTrack.addCue(vttCue);
  });

  removeDuplicateNonReloadedCues(textTrack);

  if (previousMode !== 'hidden') {
    textTrack.mode = previousMode;
  }
};
